import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';

const RecoveryPanel = () => {
  const { restoreDeletedItem } = useStory();
  const [logs, setLogs] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    try {
      const storedLogs = JSON.parse(localStorage.getItem('weaver_deletion_logs') || '[]');
      setLogs(storedLogs);
    } catch (e) {
      console.error('Failed to load logs', e);
      setLogs([]);
    }
  };

  const handleRestore = async (logItem) => {
    setLoadingId(logItem.deletedAt);
    try {
      const success = await restoreDeletedItem(logItem);
      if (success) {
        // Refresh logs
        loadLogs();
      } else {
        alert('Failed to restore item.');
      }
    } finally {
      setLoadingId(null);
    }
  };

  const clearLogs = () => {
    if (window.confirm('Are you sure you want to permanently delete all items in the recovery bin?')) {
      localStorage.removeItem('weaver_deletion_logs');
      setLogs([]);
    }
  };

  const getPreview = (log) => {
    if (!log || !log.item) return 'Unknown data';

    if (log.type === 'story_entry' || log.type === 'archived_entry') {
      return log.item.content ? (log.item.content.length > 60 ? log.item.content.substring(0, 60) + '...' : log.item.content) : 'Empty passage';
    }

    if (log.type === 'chapter_deleted') {
      return `Chapter: ${log.item.title || 'Untitled'} (${log.item.wordCount || 0} words)`;
    }

    if (log.type === 'chapter_clear') {
      const items = Array.isArray(log.item) ? log.item : [];
      return `Cleared Chapter (${items.length} entries)`;
    }

    if (log.type === 'gallery_image') {
      return log.item.baseScene || 'Gallery Image';
    }

    if (log.type === 'story_reset') {
      return `Full Story Reset: ${log.item.storyTitle || 'Untitled'}`;
    }

    return 'Complex data object';
  };

  const getTypeLabel = (type) => {
    const map = {
      'story_entry': 'Passage',
      'archived_entry': 'Archived Passage',
      'chapter_deleted': 'Deleted Chapter',
      'chapter_clear': 'Cleared Chapter',
      'gallery_image': 'Image',
      'story_reset': 'Story Reset'
    };
    return map[type] || 'Item';
  };

  const getIcon = (type) => {
    if (type.includes('entry')) return FiIcons.FiMessageSquare;
    if (type.includes('chapter')) return FiIcons.FiBook;
    if (type.includes('image')) return FiIcons.FiImage;
    if (type === 'story_reset') return FiIcons.FiRefreshCw;
    return FiIcons.FiFile;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiTrash2} /> Recovery Bin
        </h4>
        {logs.length > 0 && (
          <button
            onClick={clearLogs}
            className="text-[10px] text-red-500 hover:text-red-600 font-bold uppercase transition-colors"
          >
            Empty Bin
          </button>
        )}
      </div>

      <p className="text-xs text-gray-900 dark:text-purple-300 opacity-70 mb-4">
        Items deleted recently are stored locally in your browser. Restoring a passage will append it to your active story.
      </p>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        <AnimatePresence>
          {logs.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8 text-center border border-dashed border-gray-200 dark:border-purple-800 rounded-xl">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="mx-auto text-2xl text-gray-500 dark:text-purple-700 mb-2" />
              <p className="text-xs font-bold text-gray-600 dark:text-purple-500 uppercase tracking-wider">Bin is Empty</p>
            </motion.div>
          ) : (
            logs.map((log) => (
              <motion.div
                key={log.deletedAt}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-purple-900/30 border border-gray-100 dark:border-purple-800 rounded-xl p-3 flex flex-col gap-2"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-md">
                      <SafeIcon icon={getIcon(log.type)} className="text-xs" />
                    </div>
                    <div>
                      <h5 className="text-[10px] font-bold text-gray-900 dark:text-purple-200 uppercase">{getTypeLabel(log.type)}</h5>
                      <p className="text-[9px] text-gray-600/70">{new Date(log.deletedAt).toLocaleString()}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRestore(log)}
                    disabled={loadingId === log.deletedAt}
                    className="flex items-center gap-1 px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-[10px] font-bold transition-colors disabled:opacity-50"
                  >
                    {loadingId === log.deletedAt ? (
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
                    ) : (
                      <>
                        <SafeIcon icon={FiIcons.FiRotateCcw} /> Restore
                      </>
                    )}
                  </button>
                </div>

                <div className="bg-gray-50 dark:bg-purple-950/50 rounded-lg p-2 mt-1">
                  <p className="text-xs text-gray-900 dark:text-purple-300 italic truncate" title={getPreview(log)}>
                    "{getPreview(log)}"
                  </p>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RecoveryPanel;
